export class Environment {
  private static get CustomConfig() {
    if ('customConfig' in window) {
      return window.customConfig as Record<string, string>
    } else {
      throw new Error('CustomConfig is not defined')
    }
  }

  private static CustomSetting(key: string, acceptUndefined?: boolean): string {
    if (key in this.CustomConfig || acceptUndefined) {
      return this.CustomConfig[key] ?? ''
    } else {
      throw new Error(`${key} setting is not defined`)
    }
  }

  public static get Current() {
    const hostname = window.location.hostname.toLowerCase()
    switch (hostname) {
      case 'localhost':
        return 'development'
      case 'link-stg.interfleet.ch':
        return 'staging'
      case 'link-int.interfleet.ch':
        return 'integration'
      case 'link-demo.interfleet.ch':
        return 'demo'
      case 'link.interfleet.ch':
        return 'production'
      default:
        throw `Current environment cannot be identified: ${hostname}`
    }
  }

  public static get IndentityClientId() {
    return import.meta.env.DEV
      ? import.meta.env.VITE_APP_IDENTITY_CLIENT_ID
      : this.CustomSetting('REACT_APP_IDENTITY_CLIENT_ID')
  }

  public static get IdentityUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_AUTH_URL : this.CustomSetting('REACT_APP_AUTH_URL')
  }

  public static get ContractsApiUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_CONTRACT_URL : this.CustomSetting('REACT_APP_CONTRACT_URL')
  }

  public static get CustomersApiUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_CUSTOMER_URL : this.CustomSetting('REACT_APP_CUSTOMER_URL')
  }

  public static get DocumentsApiUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_DOCUMENT_URL : this.CustomSetting('REACT_APP_DOCUMENT_URL')
  }

  public static get NotificationsApiUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_SUPPORT_URL : this.CustomSetting('REACT_APP_SUPPORT_URL')
  }

  public static get ReportsApiUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_REPORTS_URL : this.CustomSetting('REACT_APP_REPORTS_URL')
  }

  public static get OrdersApiUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_ORDERS_URL : this.CustomSetting('REACT_APP_ORDERS_URL')
  }

  public static get LocationsApiUrl() {
    return import.meta.env.DEV ? import.meta.env.VITE_APP_LOCATIONS_URL : this.CustomSetting('REACT_APP_LOCATIONS_URL')
  }

  public static get WorkflowsApiUrl() {
    switch (this.Current) {
      case 'development':
        return import.meta.env.VITE_APP_WORKFLOWS_URL
      case 'staging':
        return 'https://workflows-stg.interfleet.ch'
      case 'integration':
        return 'https://workflows-int.interfleet.ch'
      case 'demo':
        return 'https://workflows-demo.interfleet.ch'
      case 'production':
        return 'https://workflows.interfleet.ch'
    }
  }

  public static get WorkflowsHubUrl() {
    const env = this.Current
    switch (env) {
      case 'development':
        return import.meta.env.VITE_APP_WORKFLOWS_HUB_URL
      case 'staging':
        return 'https://workflows-stg.interfleet.ch/hubs/workflows'
      case 'integration':
        return 'https://workflows-int.interfleet.ch/hubs/workflows'
      case 'demo':
        return 'https://workflows-demo.interfleet.ch/hubs/workflows'
      case 'production':
        return 'https://workflows.interfleet.ch/hubs/workflows'
    }
  }

  public static get AiDocsUrl() {
    switch (this.Current) {
      case 'development':
        return import.meta.env.VITE_APP_AIDOCS_URL
      case 'staging':
        return 'https://aii-stg.interfleet.ch'
      case 'integration':
        return 'https://aii-int.interfleet.ch'
      case 'demo':
        return 'https://aii-demo.interfleet.ch'
      case 'production':
        return 'https://aii.interfleet.ch'
    }
  }

  public static get InterfleetDriverUrl() {
    switch (this.Current) {
      case 'development':
        return 'http://localhost:3000'
      case 'staging':
        return 'https://driver-stg.interfleet.ch'
      case 'integration':
        return 'https://driver-int.interfleet.ch'
      case 'demo':
        return 'https://driver-demo.interfleet.ch'
      case 'production':
        return 'https://driver.interfleet.ch'
    }
  }

  public static get WebAppUrl() {
    switch (this.Current) {
      case 'development':
        return 'http://localhost:3000'
      case 'staging':
        return 'https://4u-stg.interfleet.ch'
      case 'integration':
        return 'https://4u-int.interfleet.ch'
      case 'demo':
        return 'https://4u-demo.interfleet.ch'
      case 'production':
        return 'https://4u.interfleet.ch'
    }
  }

  public static get MatomoSiteId() {
    switch (this.Current) {
      case 'development':
        return undefined
      case 'staging':
        return undefined
      case 'integration':
        return undefined
      case 'demo':
        return undefined
      case 'production':
        return 7
    }
  }

  public static get MatomoTrackerUrl() {
    switch (this.Current) {
      case 'development':
        return 'https://stats.interfleet.ch'
      case 'staging':
        return 'https://stats.interfleet.ch'
      case 'integration':
        return 'https://stats.interfleet.ch'
      case 'demo':
        return 'https://stats.interfleet.ch'
      case 'production':
        return 'https://stats.interfleet.ch'
    }
  }

  public static get BackendMaintenance() {
    const value = import.meta.env.DEV
      ? import.meta.env.VITE_APP_BACKEND_MAINTENANCE
      : this.CustomSetting('MAINTENANCE_ON_BACKEND_ERROR', true)
    return value.toLowerCase() === 'true'
  }
}
